import { AnyAction, ThunkAction, combineReducers, configureStore } from '@reduxjs/toolkit';
import { combineEpics, createEpicMiddleware } from 'redux-observable';
import { IS_PRODUCTION } from 'utils/globals';
import reporter from 'utils/reporter';

import { slice as canvases } from 'domains/canvases';
import { slice as cart } from 'domains/cart';
import { slice as cartBilling } from 'domains/cart_billing';
import { slice as cartProducts } from 'domains/cart_products';
import { slice as cartProductCreator } from 'domains/cart_product_creator';
import { slice as cartProductEditor } from 'domains/cart_product_editor';
import { slice as catalogs } from 'domains/catalogs';
import { slice as crops } from 'domains/crops';
import { slice as multiphotos } from 'domains/multiphotos';
import { slice as navigation } from 'domains/navigation';
import { slice as optionGroups } from 'domains/option_groups';
import { slice as photos } from 'domains/photos';
import { slice as products } from 'domains/products';
import { slice as projects } from 'domains/projects';
import { slice as savedArrangements } from 'domains/saved_arrangements';
import { slice as session } from 'domains/session';
import { slice as stageDimensions } from 'domains/stage_dimensions';
import { slice as subcatalogs } from 'domains/subcatalogs';
import { slice as svgs } from 'domains/svgs';
import { slice as textNodes } from 'domains/text_nodes';
import { slice as ui } from 'domains/ui';
import { slice as wallPreviews } from 'domains/wall_previews';
import { slice as wallPreviewRooms } from 'domains/wall_preview_rooms';

import activeEditorEpics from 'domains/active_editor/active_editor.epics';
import analyticsEpics from 'domains/analytics/analytics.epics';
import applicationEpics from 'domains/application/application.epics';
import cartEpics from 'domains/cart/cart.epics';
import cartBillingEpics from 'domains/cart_billing/cart_billing.epics';
import cartOrderOptionEpics from 'domains/cart_order_options/cart_order_options.epics';
import cartProductEpics from 'domains/cart_products/cart_products.epics';
import cartProductCreatorEpics from 'domains/cart_product_creator/cart_product_creator.epics';
import cartProductEditorEpics from 'domains/cart_product_editor/cart_product_editor.epics';
import canvasEpics from 'domains/canvases/canvases.epics';
import catalogEpics from 'domains/catalogs/catalogs.epics';
import cropEpics from 'domains/crops/crops.epics';
import customSizeEpics from 'domains/custom_sizes/custom_sizes.epics';
import optionGroupEpics from 'domains/option_groups/option_groups.epics';
import photoEpics from 'domains/photos/photos.epics';
import projectsEpics from 'domains/projects/projects.epics';
import savedArrangementsEpics from 'domains/saved_arrangements/saved_arrangements.epics';
import sessionEpics from 'domains/session/session.epics';
import subcatalogEpics from 'domains/subcatalogs/subcatalogs.epics';
import svgEpics from 'domains/svgs/svgs.epics';
import textNodeEpics from 'domains/text_nodes/text_nodes.epics';
import uiEpics from 'domains/ui/ui.epics';
import wallPreviewEpics from 'domains/wall_previews/wall_previews.epics';
import wallPreviewRoomEpics from 'domains/wall_preview_rooms/wall_preview_rooms.epics';

const enhancers = [];
if (IS_PRODUCTION) {
  const sentryReduxEnhancer = reporter.createReduxEnhancer({
    stateTransformer: (state) => {
      return {
        ...state,
        catalogs: { ...state.catalogs, entities: null, ids: null },
        subcatalogs: { ...state.subcatalogs, entities: null, ids: null },
        products: { ...state.products, entities: null, ids: null },
        optionGroups: { ...state.optionGroups, entities: null, ids: null },
        options: { ...state.options, entities: null, ids: null },
      };
    },
  });
  enhancers.push(sentryReduxEnhancer);
}

const epicMiddleware = createEpicMiddleware<AnyAction, AnyAction, RootState>();

export const reducer = combineReducers({
  [canvases.name]: canvases.reducer,
  [cart.name]: cart.reducer,
  [cartBilling.name]: cartBilling.reducer,
  [cartProducts.name]: cartProducts.reducer,
  [cartProductCreator.name]: cartProductCreator.reducer,
  [cartProductEditor.name]: cartProductEditor.reducer,
  [catalogs.name]: catalogs.reducer,
  [crops.name]: crops.reducer,
  [multiphotos.name]: multiphotos.reducer,
  [navigation.name]: navigation.reducer,
  [optionGroups.name]: optionGroups.reducer,
  [photos.name]: photos.reducer,
  [products.name]: products.reducer,
  [projects.name]: projects.reducer,
  [savedArrangements.name]: savedArrangements.reducer,
  [session.name]: session.reducer,
  [stageDimensions.name]: stageDimensions.reducer,
  [subcatalogs.name]: subcatalogs.reducer,
  [svgs.name]: svgs.reducer,
  [textNodes.name]: textNodes.reducer,
  [ui.name]: ui.reducer,
  [wallPreviews.name]: wallPreviews.reducer,
  [wallPreviewRooms.name]: wallPreviewRooms.reducer,
});

const wosStore = configureStore({
  reducer,
  middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(epicMiddleware),
  enhancers,
});

export const combinedEpics = combineEpics(
  activeEditorEpics,
  analyticsEpics,
  applicationEpics,
  cartEpics,
  cartBillingEpics,
  cartOrderOptionEpics,
  cartProductEpics,
  cartProductCreatorEpics,
  cartProductEditorEpics,
  canvasEpics,
  catalogEpics,
  cropEpics,
  customSizeEpics,
  optionGroupEpics,
  photoEpics,
  projectsEpics,
  savedArrangementsEpics,
  sessionEpics,
  subcatalogEpics,
  svgEpics,
  textNodeEpics,
  uiEpics,
  wallPreviewEpics,
  wallPreviewRoomEpics
);

epicMiddleware.run(combinedEpics);

export default wosStore;

export type RootState = ReturnType<typeof reducer>;
export type AppDispatch = typeof wosStore.dispatch;
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, AnyAction>;
